// utils/initiativeInclusion.js

const normalizeId = (id) => {
  if (id == null) return null;
  return String(id).trim().toLowerCase();
};

const getNodeId = (node) =>
  normalizeId(node?.energyTreeNodeId ?? node?.EnergyTreeNodeId ?? node?.id);

const getParentNodeId = (node) =>
  normalizeId(
    node?.parentEnergyTreeNodeId ??
      node?.ParentEnergyTreeNodeId ??
      node?.parentId,
  );

// Build Map: normalizedId -> node
const buildNodeMap = (nodes = []) => {
  const map = new Map();
  nodes.forEach((node) => {
    const id = getNodeId(node);
    if (!id) return;
    map.set(id, node);
  });
  return map;
};

// Returns [clickedId, parentId, grandParentId, ...] (all normalized)
const getAncestorIds = (nodeId, nodeMap) => {
  const ids = [];
  const normalizedId = normalizeId(nodeId);
  if (!normalizedId || !nodeMap.size) return ids;

  let current = nodeMap.get(normalizedId);
  while (current) {
    const currentId = getNodeId(current);
    if (!currentId) break;

    ids.push(currentId);

    const parentId = getParentNodeId(current);
    if (!parentId) break;

    const parentNode = nodeMap.get(parentId);
    if (!parentNode) break;

    current = parentNode;
  }

  return ids;
};

/**
 * Get all initiatives a node is part of (directly or indirectly).
 *
 * @param {string} nodeId - GUID of the clicked node
 * @param {object} energyGroup - energyGroup containing energyTreeNodes & energyInitiatives
 * @returns {Array<{ initiative: object, isDirect: boolean, viaNode?: object, viaNodeId?: string }>}
 */
export const getInitiativeInclusionsForNode = (nodeId, energyGroup) => {
  if (!energyGroup) return [];

  const nodeMap = buildNodeMap(energyGroup.energyTreeNodes || []);
  const clickedId = normalizeId(nodeId);
  if (!clickedId) return [];

  const ancestorIds = getAncestorIds(clickedId, nodeMap);
  const initiatives = energyGroup.energyInitiatives || [];
  if (!initiatives.length || !ancestorIds.length) return [];

  return initiatives
    .map((initiative) => {
      // initiative.energyTreeNodes is an array of GUID strings
      const initiativeIds = (initiative.energyTreeNodes || [])
        .map(normalizeId)
        .filter(Boolean);

      if (!initiativeIds.length) return null;

      const initiativeIdSet = new Set(initiativeIds);

      // First ancestor (self → parent → grandparent...) that this initiative references
      const matchedAncestorId = ancestorIds.find((id) =>
        initiativeIdSet.has(id),
      );

      if (!matchedAncestorId) return null;

      const isDirect = matchedAncestorId === clickedId;
      const viaNode = nodeMap.get(matchedAncestorId);

      return {
        initiative,
        isDirect,
        viaNode,
        viaNodeId: matchedAncestorId,
      };
    })
    .filter(Boolean);
};

/**
 * Simple boolean helper: is this node (or its ancestors) part of this initiative?
 *
 * @param {string} nodeId
 * @param {object} initiative
 * @param {object} energyGroup
 * @returns {boolean}
 */
export const isNodeInInitiative = (nodeId, initiative, energyGroup) => {
  if (!energyGroup || !initiative) return false;

  const nodeMap = buildNodeMap(energyGroup.energyTreeNodes || []);
  const clickedId = normalizeId(nodeId);
  if (!clickedId) return false;

  const ancestorIds = getAncestorIds(clickedId, nodeMap);

  const initiativeIds = (initiative.energyTreeNodes || [])
    .map(normalizeId)
    .filter(Boolean);

  if (!initiativeIds.length || !ancestorIds.length) return false;

  const initiativeIdSet = new Set(initiativeIds);
  return ancestorIds.some((id) => initiativeIdSet.has(id));
};
